import { Injectable } from '@angular/core';
import { SessionService } from '../session/session.service';
import { SESSION_KEYS } from 'src/app/dataModels/session-keys';
import { DashboardService } from 'src/app/pages/dashboard/dashboard.service';
import { CONSTANTS } from 'src/app/dataModels/constants';


/**
 * User Service
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * Constructor to inject dependency
   * @param sessionService instance of SessionService
   */
  constructor(private sessionService: SessionService) {}

  /**
   * User model
   */
  private user = {
    userName: '',
    userFullName: '',
    authToken: '',
    userGroup: '',
    idType: '',
    idSeq: '',
    status: '',
    firstName: '',
    lastName: '',
    middleName: '',
    defaultFacilityId: '',
    defaultFacilityName: '',
    defaultVfcPin: '',
    VfcId: '',
    profileName: '',
    profileType: '',
    patientSearchPref: '',
    userCounty: '',
    userCreated: '',
    workPhoneSeq: '',
    workPhone: '',
    workPhoneExtn: '',
    workEmailSeq: '',
    email: '',
    facilityCount: '',
    otherFundingType:'',
    env: '',
    facility: [],
    facilityList: [],
    currentFacilityId: '',
    currentFacilityName: '',
    currentVfcPin: '',
    warningMessage: ''
  };
  
  
   
  /**
   * List of left Nav
   */
  private leftNav = [];

  /**
   * List of resources
   */
  private resources = {};

  /**
   * List of sideNav
   */
  sideNav = [];

  /**
   * set user data from the login response
   * @param data userData
   * @param authToken authToken
   * @param warningMessage message to be displayed to user
   * @param status status of the login api call
   */
  setUser(data, authToken, warningMessage, status) {
    // assign the user object
    this.user = data.user;

    // user Full name
    this.user.userFullName = this.user?.lastName + ', ' + this.user?.firstName;

    // set the authtoken
    this.user.authToken = authToken;

    // warning message for the user
    this.user.warningMessage = status === CONSTANTS.TRN_WARNING ? warningMessage : '';

    // current medical facility of the user
    this.user.currentFacilityId = this.user?.defaultFacilityId;

    // current medical facility name of the user
    this.user.currentFacilityName = this.user?.defaultFacilityName;

    // current medical facility vfc pin of the user
    this.user.currentVfcPin = this.user?.defaultVfcPin || this.user?.VfcId;

    // environment DEV, TEST, STG, PREPROD, UAT, TRAIN, PROD etc...
    this.user.env = data.env;

    // navigation links for the user
    this.leftNav = this.sortNavs(data.leftNav);

    // LOVS, HEIRARCHY for the values used in the application
    this.resources = this.setResources(data.resources);

    // setting up navigation links for the user
    this.setNavData();

    // set up the list of medical facilities assigned to the user
    this.user.facilityList = this.setUpFacilityList(data.facility);

    this.user.facilityCount = data.facilityCount;

    this.user.otherFundingType = data.otherFundingType;

    // modify profile type of the user
    // currently DB return column value, change it to descr
    const profileMatchedList = this.resources['PROFILE_TYPE']?.filter(profile => {
      return profile.columnValue === this.user?.profileType;
    });

    if (profileMatchedList?.length > 0) {
      this.user.profileType = profileMatchedList[0].descr;
    }

    // storing the navigation links in SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.LINKS, this.sideNav);

    // storing the Resources (LOVs and HEIRARCHY) in the SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.RESOURCES, this.resources);

    // storing the user data in SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.USER, this.user);

    // store the components of the user
    const compAccessList = [];

    this.sessionService.setSessionData(SESSION_KEYS.COMP_ACCESS, this.parseComponents(data.leftNav, compAccessList));
  }

  /**
   * get user from the SESSION STORAGE
   */
  getUser() {
    return this.sessionService.getSessionData(SESSION_KEYS.USER);
  }

  /**
   * returns the navigation links available in the SESSION STORAGE
   */
  getNavData() {
    return this.sessionService.getSessionData(SESSION_KEYS.LINKS);
  }

  /**
   * returns the Resources (LOVs and HEIRARCHY) available in the SESSION STORAGE
   */
  getResources() {
    return this.sessionService.getSessionData(SESSION_KEYS.RESOURCES);
  }

  /**
   * returns the Component access list available in the SESSION STORAGE
   */
  getCompAccessList() {
    return this.sessionService.getSessionData(SESSION_KEYS.COMP_ACCESS);
  }

  /**
   * sorting of the navigation links based on display order in ascending order
   * @param leftNav leftnavigation items
   */
  private sortNavs(leftNav): any[] {
    for (const nav of leftNav) {
      nav.level = nav.level.sort((a, b) => {
        const d1 = +a.displayOrder;
        const d2 = +b.displayOrder;
        return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
      });
    }
    return leftNav.sort((a, b) => {
      const d1 = +a.displayOrder;
      const d2 = +b.displayOrder;
      return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
    });
  }

  /**
   * setting up the navigation links
   */
  setNavData() {
    this.sideNav = [];
    for (let i = 0; i < this.leftNav.length; i++) {
      const item = this.leftNav[i];
      if (item && item.displayName) {
        const nav = {} as any;
        nav.name = item.name;
        nav.displayName = item.displayName;
        nav.action = item.action;
        nav.displayOrder = item.displayOrder;
        nav.icon = item.icon;
        nav.isCollapsed = true;
        nav.hasSubModule = item.hasSubModule;
        if (item.level && item.hasSubModule !== 'N') {
          nav.level = [];
          for (let j = 0; j < item.level.length; j++) {
            const childItem = item.level[j];
            if (childItem && childItem.displayName) {
              const childNav = {} as any;
              childNav.name = childItem.name;
              childNav.displayName = childItem.displayName;
              childNav.action = childItem.action;
              childNav.displayOrder = childItem.displayOrder;
              childNav.icon = childItem.icon;
              childNav.isCollapsed = true;
              if (childItem.level) {
                childNav.level = [];
                for (let k = 0; k < childItem.level.length; k++) {
                  const subChildItem = childItem.level[k];
                  if (subChildItem && subChildItem.displayName) {
                    const subChildNav = {} as any;
                    subChildNav.name = subChildItem.name;
                    subChildNav.displayName = subChildItem.displayName;
                    subChildNav.action = subChildItem.action;
                    subChildNav.displayOrder = subChildItem.displayOrder;
                    subChildNav.icon = subChildItem.icon;
                    subChildNav.isCollapsed = true;
                    childNav.level.push(subChildNav);
                  } // if (subChildItem.displayName)
                } // angular.forEach(childItem.level, function(subChildItem, subChildKey)
              } // if (childItem.level)

              nav.level.push(childNav);
            } // if (childItem.displayName)
          } // end of for(let j = 0)
        }
        // Manually Add IMM 15 and Annual Immun Report. Should be removed when populated by DB
        if (nav.displayName === "State Funded Program") {
          const imm15Nav = {
              name: "imm15",
              displayName: "IMM 15",
              action: "/imm15Audit",
              displayOrder: 20,
              icon: "",
              isCollapsed: true
          };
          const annualReportNav = {
              name: "annual-report",
              displayName: "Annual Immunization Report",
              action: "/annual-report",
              displayOrder: 21,
              icon: "",
              isCollapsed: true
          }
          // Top of list
          //nav.level.unshift(imm15Nav);
          //nav.level.unshift(annualReportNav);
      }
        this.sideNav.push(nav);
      } // if (item.displayName)
    } // for (let i = 0; i < this.user?.leftNav.length; i++)
  }

  /**
   * setting up medical facility list of the user
   * @param facilityList list of facility
   */
  private setUpFacilityList(facilityList): any[] {
    if (facilityList && facilityList.length > 0) {
      // sort the facilitylist by name
      facilityList = facilityList.sort((a, b) => {
        const f1 = a.facilityName;
        const f2 = b.facilityName;
        return f1 < f2 ? -1 : f1 > f2 ? 1 : 0;
      });

      // filter out all the inactive facilities
      facilityList = facilityList.filter(facility => {
        return facility.facilityStatus !== 'INACTIVE';
      });

      // create datastructure
      for (let i = 0; i < facilityList.length; i++) {
        const facility = facilityList[i];

        if (facility.vfcPin) {
          facility.displayValue = facility.facilityId + ' - ' + facility.facilityName + ' - ' + facility.vfcPin;
        } else {
          facility.displayValue = facility.facilityId + ' - ' + facility.facilityName;
        }
      }
    }

    return facilityList;
  }

  /**
   * set the resources
   * @param resourcesList list of resources LOVs, and HEIRARCHIES
   */
  setResources(resourcesList) {
    const resources = {};

    for (const resource of resourcesList) {
      resources[resource.key] = resource.data;
    }

    return resources;
  }

  /**
   * Recursive function to get list of components avaialble for user
   * @param navList list of navigation items
   * @param compAccessList list of compAccess
   */
  parseComponents(navList, compAccessList) {
    for (const nav of navList) {
      if (nav.type.toUpperCase() === 'COMP' && nav.componentType.toUpperCase() === 'FUNCTION') {
        compAccessList.push(nav.name.toUpperCase());
      } else if (nav.type === 'NAV' && nav.level && nav.level.length > 0) {
        this.parseComponents(nav.level, compAccessList);
      }
    }

    return compAccessList;
  }

  /**
   * refresh user
   * @param data user json containing left nav and resources
   * @param selectedFacilityInfo facility information having facility name, facility id and vfcpin
   * @param isDefaultFacilityChanged indicate if default facility has been changed
   */
  public refreshUser(data, selectedFacilityInfo, isDefaultFacilityChanged = false) {
    const user = this.sessionService.getSessionData(SESSION_KEYS.USER);

    user.currentFacilityId = selectedFacilityInfo.facilityId;

    user.currentFacilityName = selectedFacilityInfo.facilityName;

    user.currentVfcPin = selectedFacilityInfo.vfcPin;

    user.otherFundingType = data.otherFundingType;

    if (isDefaultFacilityChanged) {
      user.defaultFacilityId = selectedFacilityInfo.facilityId;

      user.defaultFacilityName = selectedFacilityInfo.facilityName;

      user.defaultVfcPin = selectedFacilityInfo.vfcPin;
    }

    // navigation links for the user
    this.leftNav = this.sortNavs(data.leftNav);

    // LOVS, HEIRARCHY for the values used in the application
    const resources = this.setResources(data.resources);

    // setting up navigation links for the user
    this.setNavData();

    // storing the navigation links in SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.LINKS, this.sideNav);

    // storing the Resources (LOVs and HEIRARCHY) in the SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.RESOURCES, resources);

    // storing the user data in SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.USER, user);

    // store the components of the user
    const compAccessList = [];

    this.sessionService.setSessionData(SESSION_KEYS.COMP_ACCESS, this.parseComponents(data.leftNav, compAccessList));
  }

  /**
   * update patient search preference for the user
   * @param patientSearchPreference  patient search preference selected or updated by the user
   */
  public setUserPatientSearchPreference(patientSearchPreference) {
    const user = this.sessionService.getSessionData(SESSION_KEYS.USER);

    user.patientSearchPref = patientSearchPreference;

    // storing the user data in SESSION STORAGE
    this.sessionService.setSessionData(SESSION_KEYS.USER, user);
  }
  
    /**
   * update otherFundingType for the user
   * @param patientSearchPreference  patient search preference selected or updated by the user
   */
    public setOtherFundingType(otherFundingType) {
      const user = this.sessionService.getSessionData(SESSION_KEYS.USER);
  
      user.otherFundingType = otherFundingType;
  
      // storing the user data in SESSION STORAGE
      this.sessionService.setSessionData(SESSION_KEYS.USER, user);
    }

    /**
   * update patient search preference for the user
   * @param patientSearchPreference  patient search preference selected or updated by the user
   */
    public setCurrentVfcPin(currentVfcPin) {
      const user = this.sessionService.getSessionData(SESSION_KEYS.USER);
      user.currentVfcPin = user.currentVfcPin ? user.currentVfcPin : currentVfcPin;
      this.sessionService.setSessionData(SESSION_KEYS.USER, user);
    }
}
